import { TEAM_PERMISSION_KEYS, type TeamPermissionKey } from '@/lib/team/permissions';
import {
  effectiveCapabilitiesForLegacyKeys,
  expandWithImpliedLevels,
  isEffectiveCapabilityKey,
} from '@/lib/config-system/workspace-capabilities';

/**
 * The gate vocabulary (F5, P5-02).
 *
 * A gate — a sidebar item's `requiredPermission`, a settings definition, an
 * operations access rule, an AI tool's `minimumPermission`, an API route's
 * permission list — may name EITHER a legacy permission key (`manage_branches`)
 * or a capability key from either registry (`locations.manage`,
 * `catalog.menus.manage`).
 *
 * It has to be both. F5's point is that a gate should stop naming the catch-all,
 * and that is not expressible inside the legacy vocabulary: `manage_branches` is
 * the ONLY legacy key standing behind `locations.manage` and `delivery.manage`,
 * so there is no narrower legacy key to substitute. Splitting the gate therefore
 * means introducing capability keys, and the P5-01 compat layer is what keeps
 * existing members' legacy grants resolving through them.
 *
 * This module is deliberately free of `server-only` so both the server access
 * helpers (`lib/server/team-access.ts`) and shared eligibility code
 * (`lib/ai/business-agent/eligibility.ts`) can share ONE resolution rule.
 */

/** A gate may name a legacy permission key or a capability key. */
export type GateKey = TeamPermissionKey | string;

/**
 * Does a set of granted LEGACY permission keys satisfy `gateKey`?
 *
 * Resolution order matters and is load-bearing:
 *
 * 1. A known legacy key is answered by the exact legacy lookup, never
 *    re-interpreted. Every gate that existed before F5 names a legacy key, so this
 *    branch is the guarantee that the split cannot change a single existing
 *    authorization decision.
 * 2. Otherwise, if it is a capability key in either registry, the granted legacy
 *    keys are projected forward through `effectiveCapabilitiesForLegacyKeys`. A
 *    member holding only `manage_branches` satisfies `locations.manage`,
 *    `orders.manage`, `team.manage`, `delivery.manage` and `catering.manage`,
 *    which is exactly the "granted together on migration" rule.
 * 3. Anything else is false. An unrecognised key must never be satisfied.
 */
export function grantedKeysSatisfy(
  grantedLegacyKeys: readonly string[],
  gateKey: string,
): boolean {
  if ((TEAM_PERMISSION_KEYS as readonly string[]).includes(gateKey)) {
    return grantedLegacyKeys.includes(gateKey);
  }

  if (!isEffectiveCapabilityKey(gateKey)) return false;

  // A capability key granted DIRECTLY satisfies its own gate. This is the
  // post-migration case: once a policy grants `locations.manage`, the member's
  // granted set holds capability keys, and without this branch every capability
  // gate would fail closed and lock them out of the surface they were just given.
  const grantedCapabilities = expandWithImpliedLevels(
    grantedLegacyKeys.filter((key) => isEffectiveCapabilityKey(key)),
  );

  if (grantedCapabilities.includes(gateKey)) return true;

  // Otherwise project the legacy keys forward. A member holding only
  // `manage_branches` satisfies `locations.manage`, `orders.manage`,
  // `team.manage`, `delivery.manage` and `catering.manage` — the "granted
  // together on migration" rule.
  return effectiveCapabilitiesForLegacyKeys(grantedLegacyKeys).includes(gateKey);
}

/** The granted legacy keys of a `Record<key, boolean>` permission bag. */
export function grantedKeysOf(permissions: Record<string, unknown> | null | undefined): string[] {
  if (!permissions) return [];
  return Object.entries(permissions)
    .filter(([, value]) => Boolean(value))
    .map(([key]) => key);
}
